<template>
  <!-- `scroll.capture`: o evento de rolagem NÃO borbulha, mas a fase de captura passa por aqui
       na descida até o container do grupo deslizante. É como o bloco sabe que deslizou sem
       precisar alcançar as entranhas do v-slide-group. -->
  <div class="kanban-block" @scroll.capture="onSlide($event)">
    <!-- Cabeçalho só quando há dimensão posterior: com uma dimensão só existe um bloco, sem
         rótulo, e o quadro fica exatamente como era antes de blocos existirem. -->
    <div v-if="labels.length" class="kanban-block-header d-flex align-center flex-wrap ga-2 mb-1">
      <ontos-lane-value v-for="label of labels" :key="label.dimension.key" named
                        :value="label" :context="context" @link="$emit('link', $event)"/>
    </div>

    <!-- O invólucro troca, a coluna da faixa não: `v-row` enquanto todas cabem — e aí o
         desenho é exatamente o de sempre, a mesma linha e as mesmas colunas —, `v-slide-group`
         quando não cabem, que mantém as faixas lado a lado na largura mínima e mostra as setas
         para andar. Quem mede e decide é o tabuleiro, porque a resposta é uma só para o quadro
         inteiro: todos os blocos têm as mesmas raias.

         Componentes importados, e não nomes em string: o auto-import do webpack-plugin-vuetify
         lê as TAGS do template, e um `:is="'v-slide-group'"` não registraria nada.

         `show-arrows="desktop"` porque no telefone arrastar a tela já é o gesto natural, e a
         seta ali só disputaria espaço com a faixa. É opção do próprio componente: no desktop
         ela aparece sempre que o grupo existe — e ele só existe quando desliza. -->
    <component :is="wrapper" :class="{'kanban-lanes--slide': sliding}"
               :style="sliding ? slideVars : undefined"
               :show-arrows="sliding ? 'desktop' : undefined">
      <v-col v-for="lane in lanes" :key="lane.id" class="kanban-column">
        <ontos-kanban-lane :lane="lane" :period="periods[lane.id]"
                           :group="group" :moved-fields="movedFields"
                           :can-create="canCreate" :can-write="canWrite"
                           :create-label="createLabelOf(lane)"
                           :count-title="countTitleOf(lane)"
                           @create="$emit('create', {coordinate, lane, element: $event})"
                           @period-change="$emit('period-change', {lane, period: $event})"
                           @drag-start="$emit('drag-start', {coordinate, lane})"
                           @change="$emit('change', {event: $event, coordinate, lane})">
          <!-- A raia e a coordenada descem junto com o item: num relacionamento múltiplo o
               mesmo card está em várias, e "mover" precisa saber de qual se trata. -->
          <template #default="{ instance }">
            <slot :instance="instance" :coordinate="coordinate" :lane="lane"/>
          </template>
        </ontos-kanban-lane>
      </v-col>
    </component>
  </div>
</template>

<script lang="ts">
import {Component, Vue, Prop, Watch, toNative} from 'vue-facing-decorator'
import {VRow, VSlideGroup} from "vuetify/components"
import KanbanLane from "@/components/live/instance/KanbanLane.vue"
import LaneValue from "@/components/live/instance/LaneValue.vue"
import {BlockLabel, LaneModel} from "@/utils/kanbanGrouping"
import {Coordinate} from "@/utils/kanbanDimensions"
import {KanbanPeriods} from "@/utils/finishedPeriod"
import {laneSlideVars} from "@/utils/kanbanLayout"
import {InstanceContext} from "@/types/InstanceContext"

/** Texto que depende do workspace, e por isso chega pronto de quem hospeda. */
type LaneText = (lane: LaneModel) => string

/**
 * Um bloco do tabuleiro: o cabeçalho que diz em que combinação de valores ele está, e as
 * raias daquela combinação.
 *
 * Existe pelo mesmo motivo que a faixa existe — o sandbox de desenvolvimento desenha o mesmo
 * tabuleiro com dados fabricados, e sem o bloco a marcação da grade voltaria a ser duas
 * cópias, agora maiores. O que ele tem de próprio é o cabeçalho de rótulos, a decisão de
 * coluna e a anotação da coordenada nos eventos que sobem.
 *
 * **Não calcula nada por raia**, e isso não é preciosismo: o `v-model` do vuedraggable escreve
 * DENTRO do objeto da raia. Um computed que criasse objetos novos faria o Sortable escrever
 * num array descartável, e o card voltaria sozinho para o lugar de origem.
 *
 * Também não decide o que um gesto significa: repassa para cima com a coordenada colada, e
 * quem grava é o tabuleiro. É o mesmo contrato da faixa.
 */
@Component({
  emits: ['create', 'change', 'period-change', 'link', 'slide', 'drag-start'],
  name: 'ontos-kanban-block',
  components: {
    'ontos-kanban-lane': KanbanLane,
    'ontos-lane-value': LaneValue
  }
})
class KanbanBlock extends Vue {
  /** De onde sai o ambiente do chip de relacionamento, para ele saber que ficha abrir. */
  @Prop({type: Object as () => InstanceContext, required: true}) readonly context!: InstanceContext
  /** As raias deste bloco, já agrupadas. Passadas adiante por REFERÊNCIA — ver acima. */
  @Prop({type: Array as () => LaneModel[], required: true}) readonly lanes!: LaneModel[]
  /** Onde este bloco está nas dimensões posteriores. Vazio quando não há nenhuma. */
  @Prop({type: Object as () => Coordinate, default: () => ({})}) readonly coordinate!: Coordinate
  /** Um rótulo por dimensão posterior. Lista vazia esconde o cabeçalho inteiro. */
  @Prop({type: Array as () => BlockLabel[], default: () => []}) readonly labels!: BlockLabel[]
  /**
   * Prazo por id de raia terminal. É do QUADRO e não do bloco: a preferência é por coleção e
   * vale para todos os blocos, e é por isso que o `period-change` sobe sem a coordenada.
   */
  @Prop({type: Object as () => KanbanPeriods, default: () => ({})}) readonly periods!: KanbanPeriods
  /** Grupo de arrasto, um por coleção: é ele que permite arrastar de um bloco para outro. */
  @Prop({type: String, required: true}) readonly group!: string
  /** Repassado à raia: os campos que arrastar muda, que quem olha tem de poder editar. */
  @Prop({type: Array, default: () => []}) readonly movedFields!: string[]
  /** As faixas não cabem todas à vista, e por isso deslizam em vez de dividir a largura. */
  @Prop({type: Boolean, default: false}) readonly sliding!: boolean
  @Prop({type: Boolean, default: true}) readonly canCreate!: boolean
  /** Repassado à raia, que é quem desliga o arrasto. */
  @Prop({type: Boolean, default: true}) readonly canWrite!: boolean
  //Funções e não mapas prontos: o texto depende de conhecimento do host — o nome da coleção,
  //o idioma. No host precisam ser MÉTODOS, nunca campos com arrow: o Vue 3 entrega o método já
  //ligado à instância, e um campo com arrow capturaria o `this` da classe crua.
  @Prop({type: Function as unknown as () => LaneText, default: () => () => ''})
  readonly createLabelOf!: LaneText
  @Prop({type: Function as unknown as () => LaneText, default: () => () => ''})
  readonly countTitleOf!: LaneText
  /**
   * Onde o quadro INTEIRO está deslizado, em pixels.
   *
   * Todos os blocos recebem o mesmo valor, e é isso que os faz deslizar juntos: eles são as
   * linhas de uma grade só, e a coluna "Fazendo" de um bloco tem de ficar sobre a "Fazendo" do
   * outro. Cada bloco rolando por conta própria desalinha a grade e desfaz a comparação, que é
   * justamente o que o quadro existe para dar.
   *
   * Quem guarda a posição é o tabuleiro, e não o bloco: ela é de todos.
   */
  @Prop({type: Number, default: 0}) readonly slideOffset!: number

  /** A linha do grid, ou o grupo deslizante. Ver o comentário do template. */
  get wrapper() {
    return this.sliding ? VSlideGroup : VRow
  }

  get slideVars(): Record<string, string> {
    return laneSlideVars()
  }

  /** Um bloco que nasce depois — dimensão nova, bloco vazio revelado — já nasce no lugar. */
  mounted() {
    this.applySlide()
  }

  @Watch('slideOffset')
  onSlideOffsetChange() {
    this.applySlide()
  }

  /** Deslizou aqui: o tabuleiro leva a posição aos demais. */
  onSlide(event: Event) {
    this.$emit('slide', (event.target as HTMLElement).scrollLeft)
  }

  /**
   * Põe este bloco na posição do quadro.
   *
   * A comparação é o que corta o eco: mexer no `scrollLeft` dispara outro evento de rolagem,
   * que subiria como um `slide` novo. Já estando na posição, não se mexe e o laço morre aqui.
   */
  private applySlide() {
    const container = this.$el?.querySelector?.('.v-slide-group__container') as HTMLElement | null
    if (container && Math.round(container.scrollLeft) !== Math.round(this.slideOffset)) {
      container.scrollLeft = this.slideOffset
    }
  }
}

export default toNative(KanbanBlock)
</script>

<style scoped>
/* Um respiro entre blocos, para a leitura não colar o fim de um no cabeçalho do seguinte. */
.kanban-block + .kanban-block {
  margin-top: 16px;
}

/* Deslizando, a faixa para de dividir a largura e passa a ter a MÍNIMA — é a soma delas
   passando do container que faz o v-slide-group mostrar as setas. `flex-basis` e nada mais:
   o `.v-col` já vem com `flex-grow: 1` (sobrando espaço, ainda distribui) e `flex-shrink: 0`
   (faltando, não encolhe, que é o que obriga a deslizar em vez de apertar).

   A largura e o gutter chegam por variável, do kanbanLayout: é o mesmo número que decide
   QUANDO deslizar, e reescrevê-lo aqui faria a conta e o desenho discordarem. */
/* Sem CONTENÇÃO enquanto desliza.

   O `.v-slide-group__container` vem com `contain: content` (= layout, paint e style), que é
   dica de desempenho do Vuetify e custa caro aqui por duas razões, as duas vistas em tela:

   1. contenção de LAYOUT torna o elemento bloco de contenção de descendentes `position: fixed`
      — e o clone que o Sortable arrasta no toque é fixo, então ele saía deslocado do dedo;
   2. contenção de PINTURA isola a camada, e um elemento fixo cruzando um container que rola
      deixa ladrilho velho pintado — a faixa aparecia duplicada, fantasma, sobre a vizinha.

   O clone já foi para o `body` (ver o `fallback-on-body` da faixa), o que resolve a primeira
   pela raiz; isto resolve a segunda e serve de cinto para qualquer outro descendente fixo que
   venha a existir dentro da faixa. O que se perde é a dica de desempenho, sobre meia dúzia de
   faixas. */
.kanban-lanes--slide :deep(.v-slide-group__container) {
  contain: none;
}

.kanban-lanes--slide .kanban-column {
  flex-basis: var(--ontos-lane-min);
  /* E `flex-grow: 0`, que é o que TRAVA a faixa na mínima.

     O `.v-col` vem com `flex-grow: 1` — na linha é ele que divide a largura entre as faixas.
     Aqui fazia o contrário do esperado: o conteúdo do grupo deslizante se dimensiona pelo
     max-content, e um item que PODE crescer contribui com o max-content dele — o card mais
     largo da raia — e não com o flex-basis. O conteúdo ficava mais largo que a soma das
     mínimas, e as faixas cresciam junto, justo no modo em que deveriam estar no menor tamanho.

     Sem crescer, cada faixa é exatamente a mínima e o conteúdo é a soma delas. Encolher já
     estava descartado pelo `flex-shrink: 0` que o `.v-col` também traz. */
  flex-grow: 0;
}

/* As setas FLUTUAM sobre o quadro, em vez de ocuparem uma faixa própria.

   Absolutas, elas saem do fluxo do flex e devolvem ao conteúdo a largura inteira: as raias
   voltam a ocupar exatamente o espaço que ocupam quando não desliza, em vez de cederem 52px de
   cada lado. É também o que faz as duas formas coincidirem no ponto de virada — a mesma largura
   de faixa dos dois lados do limite, sem salto. */
.kanban-lanes--slide {
  position: relative;
  /* A MESMA margem negativa do `.v-row`, pela mesma variável: é ela que cancela o padding da
     primeira e da última coluna. Sem ela as raias ficariam 12px para dentro do resto da página,
     e o alinhamento com o título e com os botões do cabeçalho se perderia justamente no modo
     em que o espaço é escasso. */
  margin: 0 calc(var(--v-col-gap-x) * -.5);
  /* O `.v-slide-group` vem com `overflow: hidden`, e é ele que recortaria a seta ao ser
     empurrada para fora. Pode ser visível sem perigo: quem recorta a rolagem é o
     `__container`, que tem o próprio `overflow-x: auto`. */
  overflow: visible;
  /* A largura da seta, num lugar só: ela é o tamanho do alvo E o deslocamento para fora, e as
     duas coisas têm de andar juntas para a seta parar rente à raia. 28px é o alvo confortável
     que ainda cabe no branco do `v-container` — ver o cálculo abaixo. */
  --ontos-slide-arrow: 28px;
}

.kanban-lanes--slide :deep(.v-slide-group__prev),
.kanban-lanes--slide :deep(.v-slide-group__next) {
  position: absolute;
  z-index: 2;
  /* A faixa do cabeçalho da raia, somada: 3px do acento, 8px de respiro, o botão "+" de 28px e
     mais 8px. Centralizada nela, a seta fica na linha do NOME da raia — no meio da altura ela
     ficava perdida lá embaixo, e numa raia alta saía da janela. */
  top: 0;
  height: calc(3px + 8px + 28px + 8px);
  /* O min-width de 52px é o que reservava a faixa; sem ele a seta é do tamanho do alvo que
     precisa ter. */
  min-width: 0;
  width: var(--ontos-slide-arrow);
}

/* INTEIRAMENTE sobre a margem branca: quem encosta na raia é o lado INTERNO da seta, e não o
   externo na borda da tela. Ancorada assim ela não cobre nada — sai da largura da faixa e ocupa
   só o que já era vazio.

   O deslocamento é a meia-margem do gutter menos a largura da seta, as duas por variável. Com
   28px a conta dá justo os 16px do padding do `v-container`, que é o branco garantido em
   qualquer contexto: na página sobra ainda o recuo do próprio aplicativo, e dentro do modal do
   Kanban ela para exatamente na borda do card, sem nada para recortar. */
.kanban-lanes--slide :deep(.v-slide-group__prev) {
  left: calc(var(--v-col-gap-x) * .5 - var(--ontos-slide-arrow));
}

.kanban-lanes--slide :deep(.v-slide-group__next) {
  right: calc(var(--v-col-gap-x) * .5 - var(--ontos-slide-arrow));
}

/* Fundo próprio porque agora a seta pousa sobre a raia: sem ele o ícone disputaria leitura com
   o nome e com o tom do cabeçalho. É o mesmo desenho de um controle de carrossel — redondo,
   claro e com sombra rasa, para ler como "flutua acima". */
.kanban-lanes--slide :deep(.v-slide-group__prev .v-icon),
.kanban-lanes--slide :deep(.v-slide-group__next .v-icon) {
  background-color: rgba(255, 255, 255, .92);
  border-radius: 50%;
  box-shadow: 0 1px 4px rgba(0, 0, 0, .28);
}
</style>
